import { CSSProperties } from 'react';

import ContainerForm from 'components/ContainerForm';
import { InfiniteComp } from 'components/infinite-scroll/InfiniteComp';
import { InputTitle } from 'components/input/InputTitle';
import { ConfirmModal } from 'components/modal/ConfirmModal';
import { Table, TextInput, Typography, closeModal, openModal } from 'enable-ui';

import { MODAL_KEY } from 'modules/auth/core/modal-key';
import { BorderContainer } from 'modules/dashboard/styles/common';

import { ReactComponent as EmptyLibraryIcon } from 'assets/icons/empty-library-white.svg';

import { isEmpty } from 'lodash';

import { COLOR_CONFIG } from 'constants/color';

import { AddItemModal } from '../components/AddItemModal';
import { AssessmentTable } from '../components/AssessmentTable';
import { HeaderDetailPage } from '../components/HeaderDetailPage';
import { LessonTable } from '../components/LessonTable';
import { getAssessmentsInCourseColumn, getLessonsInCourseColumn } from '../core/columns';
import { useCoursesFormController } from '../core/controller/courses-form';

export const AddCoursePage = () => {
  const {
    lessonsData,
    courseId,
    values,
    disabled,
    handleChange,
    handleSubmit,
    searchValue,
    onChangeSearchValue,
    handleAddLessonsToCourse,
    selectedData,
    handleDeleteCourse,
    onCancel,
    disabledButton,
    onChangeSelectedLessons,
    selectedLessons,
    handleDeleteLessonInCourse,
    courseDetail,
    goBack,
    onDragEnd,
    assessmentData,
    onChangeSelectedAssessments,
    selectedAssessments,
    searchAssessment,
    onChangeSearchAssessment,
    handleAddAssessmentsToCourse,
    selectedAssessmentData,
    handleDeleteAssessmentInCourse,
    onDragEndAssessment,
    hasMoreLesson,
    handleLoadMoreLesson,
    hasMoreAssessment,
    handleLoadMoreAssessment,
  } = useCoursesFormController();

  const columns = getLessonsInCourseColumn(onChangeSelectedLessons, selectedLessons);

  const assessmentColumns = getAssessmentsInCourseColumn(
    onChangeSelectedAssessments,
    selectedAssessments,
  );

  const generateSaveRequirementMessage = (title: string) => {
    const typoStyle: CSSProperties = {
      padding: '8px 0 ',
    };

    return (
      <Typography
        style={typoStyle}
        className="text-center"
        size={14}
        height={20}
        color={`${COLOR_CONFIG.red}`}
      >
        Add at least 1 {title} to save
      </Typography>
    );
  };

  return (
    <ContainerForm
      onCancel={onCancel}
      onSubmit={handleSubmit}
      disabled={courseId ? disabledButton : disabled}
      titleSubmit={courseId ? 'Save' : 'Add'}
      titleCancel={courseId && 'Cancel changes'}
      disabledCancel={courseId ? disabledButton : false}
    >
      <HeaderDetailPage
        title={courseId ? String(courseDetail?.name) : 'Add course'}
        showAction={Boolean(courseId)}
        actions={[
          {
            label: 'Delete',
            onClick: () => openModal(MODAL_KEY.DELETE_COURSE),
          },
        ]}
        goBack={goBack}
      />
      <BorderContainer spacing="md" placement="bottom" type="margin">
        <div className="max-w-800">
          <InputTitle
            title={
              <Typography height={28} color="#222" weight={500} size={20}>
                Course name
              </Typography>
            }
          />
          {!courseId && (
            <Typography
              style={{ margin: '2px 0 6px 0' }}
              size={14}
              weight={600}
              height={20}
              color={COLOR_CONFIG['neutral-text']}
            >
              Course name
            </Typography>
          )}
          <TextInput
            name="name"
            className="max-w-800 rounded-12"
            value={values.name}
            onChange={handleChange}
            maxLength={80}
          />
        </div>
      </BorderContainer>

      <BorderContainer spacing="md" placement="bottom" type="margin">
        <InputTitle
          extraClass="mb-16"
          title={
            <Typography height={28} color="#222" weight={500} size={20}>
              Lessons
            </Typography>
          }
        />

        <div>
          {!isEmpty(selectedData) ? (
            <LessonTable
              lessons={selectedData}
              onDelete={handleDeleteLessonInCourse}
              onDragEnd={onDragEnd}
            />
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <EmptyLibraryIcon />
              <Typography size={14} height={20} color={COLOR_CONFIG['neutral-text']}>
                No lesson was added yet.
              </Typography>
            </div>
          )}

          <AddItemModal
            title="Add lesson"
            selectedData={selectedData}
            onAdd={handleAddLessonsToCourse}
            placeholderSearch="Search by lesson"
            options={lessonsData}
            searchValue={searchValue.value}
            onChange={onChangeSearchValue}
            formMode={courseId && !isEmpty(selectedData) ? 'edit' : 'add'}
            isShowAddFormButton={true}
            content={
              <InfiniteComp hasMore={hasMoreLesson.value} handleLoadMore={handleLoadMoreLesson}>
                <Table
                  columns={columns}
                  dataSource={lessonsData}
                  total={99999}
                  tableKey={''}
                  queryState={{
                    page: 1,
                    limit: 99999,
                  }}
                  paginationConfigs={{
                    show: false,
                  }}
                  headerConfigs={{
                    show: false,
                  }}
                />
              </InfiniteComp>
            }
          />

          {isEmpty(selectedData) && generateSaveRequirementMessage('lesson')}
        </div>
      </BorderContainer>

      <BorderContainer spacing="md" placement="bottom" type="margin">
        <InputTitle
          unsetMarginBottom
          title={
            <Typography height={28} color="#222" weight={500} size={20}>
              Assessments
            </Typography>
          }
        />

        {courseId && (
          <Typography className="mb-16" size={14} height={20} color={COLOR_CONFIG['neutral-text']}>
            You can add up to 3 assessments.
          </Typography>
        )}

        <div>
          {!isEmpty(selectedAssessmentData) ? (
            <AssessmentTable
              assessments={selectedAssessmentData}
              onDelete={handleDeleteAssessmentInCourse}
              onDragEnd={onDragEndAssessment}
            />
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <EmptyLibraryIcon />
              <Typography size={14} height={20} color={COLOR_CONFIG['neutral-text']}>
                No assessment was added yet.
              </Typography>

              {!courseId && (
                <Typography size={14} height={20} color={COLOR_CONFIG['neutral-text']}>
                  You can add up to 3 assessments.
                </Typography>
              )}
            </div>
          )}

          <AddItemModal
            title="Add assessment"
            selectedData={selectedAssessmentData}
            onAdd={handleAddAssessmentsToCourse}
            placeholderSearch="Search by assessment"
            isShowAddFormButton={true}
            options={assessmentData}
            searchValue={searchAssessment.value}
            onChange={onChangeSearchAssessment}
            formMode={courseId && !isEmpty(selectedAssessmentData) ? 'edit' : 'add'}
            content={
              <InfiniteComp
                hasMore={hasMoreAssessment.value}
                handleLoadMore={handleLoadMoreAssessment}
              >
                <Table
                  columns={assessmentColumns}
                  dataSource={assessmentData}
                  total={99999}
                  tableKey={''}
                  queryState={{
                    page: 1,
                    limit: 99999,
                  }}
                  paginationConfigs={{
                    show: false,
                  }}
                  headerConfigs={{
                    show: false,
                  }}
                />
              </InfiniteComp>
            }
          />

          {isEmpty(selectedAssessmentData) && generateSaveRequirementMessage('assessment')}
        </div>
      </BorderContainer>

      <ConfirmModal
        modalKey={MODAL_KEY.DELETE_COURSE}
        content={'Delete these course.'}
        header={`Delete course`}
        onCancel={() => closeModal(MODAL_KEY.DELETE_COURSE)}
        onSubmit={handleDeleteCourse}
      />
    </ContainerForm>
  );
};
